import React, { useState, useEffect, useContext } from "react";
import DashboardLayout from "../../../layoutcontainers/DashboardLayout";
import { useNavigate, useSearchParams } from "react-router-dom";
import BASEURL from "../../Utils/Baseurl";
import axios from "axios";
import {
  Card,
  CardContent,
  CardHeader,
  Stack,
  Divider,
  IconButton,
  Tooltip,
  Chip,
  Switch,
  FormControlLabel,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import MaterialTypography from "../../../components/MaterialTypography";
import MaterialButton from "../../../components/MaterialButton";
import MaterialInput from "../../../components/MaterialInput";
import MaterialSelect from "../../../components/MasterSelect";
import UserDetailsContext from "../../../context/auth";
import ConfirmationDialog from "../../../components/Dialog/ConfirmationDialog";
import Swal from "sweetalert2";
import {
  getLatePolicyConfigurationById,
  createLatePolicyConfigurationMutation,
  updateLatePolicyConfigurationMutation,
  deleteLatePolicyConfigurationMutation,
  employeeTypeOptionapi,
  allCategoriesQuery,
} from "./api";

const OT_LATE_DEDUCTION_OPTIONS = [
  { value: "none", label: "No Deduction" },
  { value: "daily", label: "Daily (per day)" },
  { value: "monthly", label: "Monthly (total)" },
  { value: "ot_absorb", label: "OT Absorb (late deducted from OT first)" },
];

const SLAB_TYPE_OPTIONS = [
  { value: "daily", label: "Daily (Per Occurrence)" },
  { value: "monthly", label: "Monthly (Accumulation)" },
];

const ACTION_OPTIONS = [
  { value: "warning", label: "Warning Only" },
  { value: "half_day_lop", label: "Half Day LOP" },
  { value: "full_day_lop", label: "Full Day LOP" },
];

const ACTION_LOP_MAP = {
  warning: 0,
  half_day_lop: 0.5,
  full_day_lop: 1.0,
};

const AddLatePolicyConfiguration = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get("id");
  const { user } = useContext(UserDetailsContext);

  const [togglePage, setTogglePage] = useState({ view: false, edit: false });
  const [employeeTypeOptions, setEmployeeTypeOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);

  const [policyData, setPolicyData] = useState({
    name: "",
    nameStatus: "unchecked",
    employeeType: "",
    employeeTypeStatus: "unchecked",
    category: "",
    categoryStatus: "unchecked",
    isActive: true,
    otLateDeductionMode: "none",
  });

  const [slabs, setSlabs] = useState([]);

  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    message: "",
    onConfirm: null,
  });

  useEffect(() => {
    fetchDropdowns();
    if (editId) {
      fetchPolicyById(editId);
      setTogglePage({ view: true, edit: false });
    }
  }, [editId]);

  const fetchDropdowns = async () => {
    try {
      const [empRes, catRes] = await Promise.all([
        axios.post(BASEURL, { query: employeeTypeOptionapi }, { headers: { "Content-Type": "application/json" } }),
        axios.post(BASEURL, { query: allCategoriesQuery }, { headers: { "Content-Type": "application/json" } }),
      ]);

      const empTypes = empRes.data.data.allEmployeeTypes || [];
      setEmployeeTypeOptions(
        empTypes.map((e) => ({ value: e.employeetype, label: e.employeetype }))
      );

      const cats = catRes.data.data.allCategories || [];
      setCategoryOptions(
        cats.filter((c) => c && c.trim()).map((c) => ({ value: c, label: c }))
      );
    } catch (err) {
      console.error("Error fetching dropdowns:", err);
    }
  };

  const fetchPolicyById = async (id) => {
    try {
      const response = await axios.post(
        BASEURL,
        { query: getLatePolicyConfigurationById, variables: { id } },
        { headers: { "Content-Type": "application/json" } }
      );
      const data = response.data.data.latePolicyConfigurationById;
      if (data) {
        setPolicyData({
          name: data.name || "",
          nameStatus: "success",
          employeeType: data.employeeType || "",
          employeeTypeStatus: "success",
          category: data.category || "",
          categoryStatus: "success",
          isActive: data.isActive !== false,
          otLateDeductionMode: (data.otLateDeductionMode || "none").toLowerCase(),
        });
        setSlabs(
          (data.slabs || []).map((s, i) => ({
            key: i,
            slabType: s.slabType.toLowerCase(),
            fromMinutes: s.fromMinutes,
            toMinutes: s.toMinutes,
            action: s.action.toLowerCase(),
            lopDays: parseFloat(s.lopDays),
          }))
        );
      }
    } catch (err) {
      console.error("Error fetching policy:", err);
    }
  };

  const handleChange = (field, value) => {
    let status = "unchecked";
    if (field === "name") {
      status = value.trim() ? "success" : "error";
    } else {
      status = "success";
    }
    setPolicyData((prev) => ({
      ...prev,
      [field]: value,
      [`${field}Status`]: status,
    }));
  };

  const addSlab = () => {
    setSlabs((prev) => [
      ...prev,
      {
        key: Date.now(),
        slabType: "daily",
        fromMinutes: 0,
        toMinutes: 0,
        action: "warning",
        lopDays: 0,
      },
    ]);
  };

  const removeSlab = (index) => {
    setSlabs((prev) => prev.filter((_, i) => i !== index));
  };

  const updateSlab = (index, field, value) => {
    setSlabs((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      // Auto-fill lopDays when action changes
      if (field === "action") {
        updated[index].lopDays = ACTION_LOP_MAP[value] ?? 0;
      }
      return updated;
    });
  };

  const handleSubmit = () => {
    if (!policyData.name.trim()) {
      Swal.fire("Error", "Policy name is required.", "error");
      return;
    }

    setConfirmDialog({
      open: true,
      title: editId ? "Update Policy" : "Create Policy",
      message: editId
        ? "Are you sure you want to update this late policy?"
        : "Are you sure you want to create this late policy?",
      onConfirm: editId ? submitUpdate : submitCreate,
    });
  };

  const submitCreate = async () => {
    setConfirmDialog({ ...confirmDialog, open: false });
    try {
      const variables = {
        name: policyData.name,
        employeeType: policyData.employeeType || null,
        category: policyData.category || null,
        isActive: policyData.isActive,
        otLateDeductionMode: policyData.otLateDeductionMode || "none",
        slabs: slabs.map((s) => ({
          slabType: s.slabType,
          fromMinutes: parseInt(s.fromMinutes) || 0,
          toMinutes: parseInt(s.toMinutes) || 0,
          action: s.action,
          lopDays: parseFloat(s.lopDays) || 0,
        })),
      };

      const response = await axios.post(
        BASEURL,
        { query: createLatePolicyConfigurationMutation, variables },
        { headers: { "Content-Type": "application/json" } }
      );

      const result = response.data.data.createLatePolicyConfiguration;
      if (result.success) {
        Swal.fire("Success", result.message, "success");
        navigate("/late-policy-configuration");
      } else {
        Swal.fire("Error", result.message, "error");
      }
    } catch (err) {
      console.error("Error creating policy:", err);
      Swal.fire("Error", "Failed to create policy.", "error");
    }
  };

  const submitUpdate = async () => {
    setConfirmDialog({ ...confirmDialog, open: false });
    try {
      const variables = {
        id: editId,
        name: policyData.name,
        employeeType: policyData.employeeType || null,
        category: policyData.category || null,
        isActive: policyData.isActive,
        otLateDeductionMode: policyData.otLateDeductionMode || "none",
        slabs: slabs.map((s) => ({
          slabType: s.slabType,
          fromMinutes: parseInt(s.fromMinutes) || 0,
          toMinutes: parseInt(s.toMinutes) || 0,
          action: s.action,
          lopDays: parseFloat(s.lopDays) || 0,
        })),
      };

      const response = await axios.post(
        BASEURL,
        { query: updateLatePolicyConfigurationMutation, variables },
        { headers: { "Content-Type": "application/json" } }
      );

      const result = response.data.data.updateLatePolicyConfiguration;
      if (result.success) {
        Swal.fire("Success", result.message, "success");
        navigate("/late-policy-configuration");
      } else {
        Swal.fire("Error", result.message, "error");
      }
    } catch (err) {
      console.error("Error updating policy:", err);
      Swal.fire("Error", "Failed to update policy.", "error");
    }
  };

  const handleDelete = () => {
    setConfirmDialog({
      open: true,
      title: "Delete Policy",
      message: "Are you sure you want to delete this late policy? This action cannot be undone.",
      onConfirm: submitDelete,
    });
  };

  const submitDelete = async () => {
    setConfirmDialog({ ...confirmDialog, open: false });
    try {
      const response = await axios.post(
        BASEURL,
        {
          query: deleteLatePolicyConfigurationMutation,
          variables: { id: editId },
        },
        { headers: { "Content-Type": "application/json" } }
      );

      const result = response.data.data.deleteLatePolicyConfiguration;
      if (result.success) {
        Swal.fire("Deleted", result.message, "success");
        navigate("/late-policy-configuration");
      } else {
        Swal.fire("Error", result.message, "error");
      }
    } catch (err) {
      console.error("Error deleting policy:", err);
      Swal.fire("Error", "Failed to delete policy.", "error");
    }
  };

  const isViewMode = togglePage.view && !togglePage.edit;
  const isEditMode = togglePage.view && togglePage.edit;
  const isAddMode = !togglePage.view && !togglePage.edit;
  const isDisabled = isViewMode;

  return (
    <DashboardLayout>
      <Card>
        <CardHeader
          title={
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <MaterialTypography variant="h5" fontWeight="medium">
                {isAddMode ? "Add Late Policy" : isEditMode ? "Edit Late Policy" : "Late Policy Details"}
              </MaterialTypography>
              <Stack direction="row" spacing={1}>
                {isViewMode && (
                  <>
                    <MaterialButton
                      variant="gradient"
                      color="dark"
                      size="small"
                      onClick={() => setTogglePage({ view: true, edit: true })}
                    >
                      Edit
                    </MaterialButton>
                    <MaterialButton
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={handleDelete}
                    >
                      Delete
                    </MaterialButton>
                  </>
                )}
                {isEditMode && (
                  <>
                    <MaterialButton
                      variant="gradient"
                      color="dark"
                      size="small"
                      onClick={handleSubmit}
                    >
                      Update
                    </MaterialButton>
                    <MaterialButton
                      variant="outlined"
                      color="dark"
                      size="small"
                      onClick={() => {
                        setTogglePage({ view: true, edit: false });
                        fetchPolicyById(editId);
                      }}
                    >
                      Discard
                    </MaterialButton>
                  </>
                )}
                {isAddMode && (
                  <MaterialButton
                    variant="gradient"
                    color="dark"
                    size="small"
                    onClick={handleSubmit}
                  >
                    Submit
                  </MaterialButton>
                )}
              </Stack>
            </Stack>
          }
        />
        <CardContent>
          {/* Policy Header Fields */}
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <MaterialInput
                label="Policy Name"
                name="name"
                value={policyData.name}
                onChange={(e) => handleChange("name", e.target.value)}
                disabled={isDisabled}
                error={policyData.nameStatus === "error"}
                success={policyData.nameStatus === "success"}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <MaterialSelect
                label="Employee Type"
                name="employeeType"
                options={[{ value: "", label: "All" }, ...employeeTypeOptions]}
                value={
                  employeeTypeOptions.find((o) => o.value === policyData.employeeType) ||
                  { value: "", label: "All" }
                }
                onChange={(selected) => handleChange("employeeType", selected?.value || "")}
                isDisabled={isDisabled}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <MaterialSelect
                label="Category"
                name="category"
                options={[{ value: "", label: "All" }, ...categoryOptions]}
                value={
                  categoryOptions.find((o) => o.value === policyData.category) ||
                  { value: "", label: "All" }
                }
                onChange={(selected) => handleChange("category", selected?.value || "")}
                isDisabled={isDisabled}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={policyData.isActive}
                    onChange={(e) =>
                      setPolicyData((prev) => ({ ...prev, isActive: e.target.checked }))
                    }
                    disabled={isDisabled}
                    color="success"
                  />
                }
                label={policyData.isActive ? "Active" : "Inactive"}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} mt={1}>
            <Grid size={{ xs: 12, md: 4 }}>
              <MaterialSelect
                label="OT Late Deduction Mode"
                name="otLateDeductionMode"
                options={OT_LATE_DEDUCTION_OPTIONS}
                value={
                  OT_LATE_DEDUCTION_OPTIONS.find((o) => o.value === policyData.otLateDeductionMode) ||
                  OT_LATE_DEDUCTION_OPTIONS[0]
                }
                onChange={(selected) =>
                  setPolicyData((prev) => ({ ...prev, otLateDeductionMode: selected?.value || "none" }))
                }
                isDisabled={isDisabled}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Slabs Section */}
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <MaterialTypography variant="h6" fontWeight="medium">
              Late Policy Slabs
            </MaterialTypography>
            {!isDisabled && (
              <MaterialButton
                variant="outlined"
                color="dark"
                size="small"
                startIcon={<AddIcon />}
                onClick={addSlab}
              >
                Add Slab
              </MaterialButton>
            )}
          </Stack>

          {/* Slab Header */}
          {slabs.length > 0 && (
            <Grid container spacing={1} mb={1}>
              <Grid size={{ xs: 2.5 }}>
                <MaterialTypography variant="caption" fontWeight="bold" color="text">
                  Slab Type
                </MaterialTypography>
              </Grid>
              <Grid size={{ xs: 1.5 }}>
                <MaterialTypography variant="caption" fontWeight="bold" color="text">
                  From (min/count)
                </MaterialTypography>
              </Grid>
              <Grid size={{ xs: 1.5 }}>
                <MaterialTypography variant="caption" fontWeight="bold" color="text">
                  To (min/count)
                </MaterialTypography>
              </Grid>
              <Grid size={{ xs: 2.5 }}>
                <MaterialTypography variant="caption" fontWeight="bold" color="text">
                  Action
                </MaterialTypography>
              </Grid>
              <Grid size={{ xs: 2 }}>
                <MaterialTypography variant="caption" fontWeight="bold" color="text">
                  LOP Days
                </MaterialTypography>
              </Grid>
              <Grid size={{ xs: 1 }}></Grid>
            </Grid>
          )}

          {/* Slab Rows */}
          {slabs.map((slab, index) => (
            <Grid container spacing={1} key={slab.key || index} mb={1} alignItems="center">
              <Grid size={{ xs: 2.5 }}>
                <MaterialSelect
                  options={SLAB_TYPE_OPTIONS}
                  value={SLAB_TYPE_OPTIONS.find((o) => o.value === slab.slabType)}
                  onChange={(selected) => updateSlab(index, "slabType", selected?.value)}
                  isDisabled={isDisabled}
                />
              </Grid>
              <Grid size={{ xs: 1.5 }}>
                <MaterialInput
                  type="number"
                  value={slab.fromMinutes}
                  onChange={(e) => updateSlab(index, "fromMinutes", e.target.value)}
                  disabled={isDisabled}
                />
              </Grid>
              <Grid size={{ xs: 1.5 }}>
                <MaterialInput
                  type="number"
                  value={slab.toMinutes}
                  onChange={(e) => updateSlab(index, "toMinutes", e.target.value)}
                  disabled={isDisabled}
                />
              </Grid>
              <Grid size={{ xs: 2.5 }}>
                <MaterialSelect
                  options={ACTION_OPTIONS}
                  value={ACTION_OPTIONS.find((o) => o.value === slab.action)}
                  onChange={(selected) => updateSlab(index, "action", selected?.value)}
                  isDisabled={isDisabled}
                />
              </Grid>
              <Grid size={{ xs: 2 }}>
                <MaterialInput
                  type="number"
                  value={slab.lopDays}
                  onChange={(e) => updateSlab(index, "lopDays", e.target.value)}
                  disabled={isDisabled}
                  inputProps={{ step: "0.5", min: "0" }}
                />
              </Grid>
              <Grid size={{ xs: 1 }}>
                {!isDisabled && (
                  <Tooltip title="Remove slab">
                    <IconButton color="error" onClick={() => removeSlab(index)} size="small">
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </Grid>
            </Grid>
          ))}

          {slabs.length === 0 && (
            <Stack alignItems="center" py={3}>
              <MaterialTypography variant="body2" color="text">
                No slabs configured. {!isDisabled && 'Click "Add Slab" to define late penalty rules.'}
              </MaterialTypography>
            </Stack>
          )}
        </CardContent>
      </Card>

      <ConfirmationDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        showConfirm={true}
        showCancel={true}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog({ ...confirmDialog, open: false })}
      />
    </DashboardLayout>
  );
};

export default AddLatePolicyConfiguration;
